import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

export default function CreateChildProfile() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const isEditMode = Boolean(childId);
  const [formData, setFormData] = useState({
    fullName: "",
    ageYears: "",
    ageMonths: "",
    gender: "Male",
    weightValue: "",
    weightUnit: "kg",
    heightValue: "",
    heightUnit: "in",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    prematureBirth: "No",
    weeksPrematurity: "",
    birthWeight: "",
    birthWeightUnit: "kg",
    healthConditions: ""
  });

  const [isPregnancy, setIsPregnancy] = useState(false);
  const [dateError, setDateError] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [loading, setLoading] = useState(isEditMode); // Set loading to true in edit mode
  const [error, setError] = useState(null);

  const updateSource = useRef(null);

  const handleAgeChange = (field, value) => {
    updateSource.current = 'age';
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDobChange = (field, value) => {
    updateSource.current = 'dob';
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const { ageYears, ageMonths, dobDay, dobMonth, dobYear } = formData;

  // Age to DOB calculation
  useEffect(() => {
    if (updateSource.current !== 'age') return;
    
    if (ageYears !== '' && ageMonths !== '') {
      const today = new Date();
      const totalMonthsAgo = parseInt(ageYears, 10) * 12 + parseInt(ageMonths, 10);
      
      const dob = new Date();
      dob.setMonth(today.getMonth() - totalMonthsAgo);

      setFormData(prev => ({
        ...prev,
        dobDay: dob.getDate(),
        dobMonth: dob.getMonth() + 1,
        dobYear: dob.getFullYear(),
      }));
    }
    updateSource.current = null;
  }, [ageYears, ageMonths]);

  useEffect(() => {
    if (dobDay && dobMonth && dobYear) {
      const selectedDate = new Date(dobYear, dobMonth - 1, dobDay);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare dates only

      if (isPregnancy) {
        if (selectedDate < today) {
          setDateError("Tentative date cannot be in the past.");
        } else {
          setDateError(null);
        }
      } else {
        if (selectedDate > today) {
          setDateError("Date of birth cannot be in the future.");
        } else {
          setDateError(null);
        }
      }
    } else {
      setDateError(null);
    }
  }, [dobDay, dobMonth, dobYear, isPregnancy]);

  // DOB to Age calculation
  useEffect(() => {
    if (updateSource.current !== 'dob') return;

    if (dobDay && dobMonth && dobYear) {
      const dob = new Date(dobYear, dobMonth - 1, dobDay);
      if (isNaN(dob.getTime()) || dob.getFullYear() !== Number(dobYear) || dob.getMonth() !== Number(dobMonth) - 1 || dob.getDate() !== Number(dobDay)) return;

      const today = new Date();
      let monthsDiff = (today.getFullYear() - dob.getFullYear()) * 12;
      monthsDiff -= dob.getMonth();
      monthsDiff += today.getMonth();
      if (today.getDate() < dob.getDate()) {
        monthsDiff--;
      }
      
      const years = Math.floor(monthsDiff / 12);
      const months = monthsDiff % 12;

      setFormData(prev => ({ ...prev, ageYears: years, ageMonths: months }));
    }
    updateSource.current = null;
  }, [dobDay, dobMonth, dobYear]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const accessToken = localStorage.getItem('access');
      try {
        if (isEditMode) {
          const childResponse = await api.get(`/children/profiles/${childId}/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const child = childResponse.data;
          setFormData({
            fullName: `${child.first_name} ${child.last_name}`,
            ageYears: Math.floor(child.age_in_months / 12),
            ageMonths: child.age_in_months % 12,
            gender: child.sex === 'M' ? 'Male' : 'Female',
            weightValue: child.weight_kg,
            weightUnit: 'kg',
            heightValue: Math.floor(child.height_cm / 30.48),
            heightUnit: 'in',
            dobDay: new Date(child.dob).getDate(),
            dobMonth: new Date(child.dob).getMonth() + 1,
            dobYear: new Date(child.dob).getFullYear(),
            prematureBirth: child.is_premature ? 'Yes' : 'No',
            weeksPrematurity: child.weeks_premature,
            birthWeight: child.birth_weight,
            birthWeightUnit: 'kg',
            healthConditions: child.notes,
          });
          if (child.profile_image) {
            setProfileImage(child.profile_image);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error);
      } finally {
        if (isEditMode) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [childId, isEditMode]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const [firstName, ...lastNameParts] = formData.fullName.split(' ');
    const lastName = lastNameParts.join(' ');

    const payload = {
      pregnancystage: isPregnancy,
      first_name: firstName || '',
      last_name: lastName || '',
      sex: formData.gender,
      weight: formData.weightValue,
      weight_unit: formData.weightUnit,
      height: `${formData.heightValue} ${formData.heightUnit}`,
      height_unit: formData.heightUnit,
      dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
      is_premature: formData.prematureBirth === 'Yes',
      weeks_premature: formData.weeksPrematurity,
      birth_weight: formData.birthWeight,
      birth_weight_unit: formData.birthWeightUnit,
      health_conditions: formData.healthConditions,
    };

    const data = new FormData();
    for (const key in payload) {
      if (payload[key] !== undefined && payload[key] !== null) {
        data.append(key, payload[key]);
      }
    }

    if (profileImageFile) {
      data.append("profile_image_upload", profileImageFile);
    }

    try {
      const accessToken = localStorage.getItem('access');
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = isEditMode
        ? await api.patch(`/children/profiles/${childId}/`, data, config)
        : await api.post("/children/profiles/", data, config);

      console.log("Form submitted:", response.data);

      if (isEditMode) {
        navigate(-1);
      } else {
        navigate("/congrats");
      }
    } catch (error) {
      setError(error.response ? error.response.data : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen={true} text="Loading Profile..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500">Error fetching data. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {loading && <LoadingSpinner />}
      <div className="bg-white lg:shadow-sm">
        <div className="lg:mx-auto lg:max-w-7xl px-5 lg:px-8 pt-6 pb-4 lg:py-4">
          <button onClick={() => navigate(-1)} className="text-gray-800 hover:text-gray-600 lg:mb-0 mb-6">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1e3a5f] lg:hidden">Create Your Child's Profile</h1>
        </div>
      </div>

      <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:py-8">
        <div className="hidden lg:block mb-8">
          <h1 className="text-3xl font-bold text-[#1e3a5f]">Create Your Child's Profile</h1>
          <p className="mt-2 text-gray-600">Please fill in the details below to create a comprehensive profile</p>
        </div>

        <div className="bg-white lg:rounded-2xl lg:shadow-sm lg:p-10 px-5 pb-32 lg:pb-0">
          <div className="mb-6 lg:mb-10 flex justify-center lg:justify-start pt-4 lg:pt-0">
            <div className="relative">
              <div className="h-32 w-32 overflow-hidden rounded-full bg-gradient-to-br from-[#FDB913] to-[#FFA500] p-2">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Child"
                    className="h-full w-full rounded-full object-cover"
                    onLoad={() => console.log(`Profile image loaded: ${profileImage}`)}
                    onError={(e) => console.error(`Error loading profile image:`, e.target.src)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-full bg-white">
                    <svg className="h-16 w-16 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                )}
              </div>
              <label className="absolute bottom-0 right-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#1e3a5f] shadow-lg hover:bg-[#152e4a] transition">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              </label>
            </div>
          </div>

          <div className="lg:space-y-8">
            <div className="flex justify-center lg:justify-start mb-4">
              <div className="flex rounded-lg p-1 bg-gray-200">
                <button
                  onClick={() => setIsPregnancy(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${!isPregnancy ? 'bg-white text-[#1e3a5f] shadow' : 'text-gray-600'}`}
                >
                  Born Child
                </button>
                <button
                  onClick={() => setIsPregnancy(true)}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${isPregnancy ? 'bg-white text-[#1e3a5f] shadow' : 'text-gray-600'}`}
                >
                  Pregnancy Stage
                </button>
              </div>
            </div>

            <div className="hidden lg:block">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4">Basic Information</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              <div className="lg:col-span-3">
                <label className="mb-2 block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  placeholder="Enter full name"
                />
              </div>

              {!isPregnancy && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Age</label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={formData.ageYears}
                    onChange={(e) => handleAgeChange('ageYears', e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">Years</option>
                    {[...Array(18)].map((_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                  <select
                    value={formData.ageMonths}
                    onChange={(e) => handleAgeChange('ageMonths', e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">Months</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Gender</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, gender: "Male" })}
                    className={`rounded-xl py-3 font-medium transition ${
                      formData.gender === "Male"
                        ? "bg-[#1e3a5f] text-white lg:shadow-md"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    Male
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, gender: "Female" })}
                    className={`rounded-xl py-3 font-medium transition ${
                      formData.gender === "Female"
                        ? "bg-pink-100 text-[#1e3a5f] border border-pink-200 lg:shadow-md"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">{isPregnancy ? "Tentative Date of Birth" : "Date of Birth"}</label>
                <div className="grid grid-cols-3 gap-3">
                  <select
                    value={formData.dobDay}
                    onChange={(e) => handleDobChange('dobDay', e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">dd</option>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  <select
                    value={formData.dobMonth}
                    onChange={(e) => handleDobChange('dobMonth', e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">mm</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  <select
                    value={formData.dobYear}
                    onChange={(e) => handleDobChange('dobYear', e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">YYYY</option>
                    {isPregnancy
                      ? [...Array(3)].map((_, i) => {
                          const year = new Date().getFullYear() + i;
                          return <option key={year} value={year}>{year}</option>;
                        })
                      : [...Array(20)].map((_, i) => {
                          const year = new Date().getFullYear() - i;
                          return <option key={year} value={year}>{year}</option>;
                        })}
                  </select>
                </div>
                {dateError && <p className="text-red-500 text-xs mt-1">{dateError}</p>}
              </div>
            </div>

            {!isPregnancy && (
            <>
            <div className="hidden lg:block border-t pt-8 mt-8">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4">Physical Measurements</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-4 lg:mt-0">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Weight</label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.weightValue}
                    onChange={(e) => setFormData({ ...formData, weightValue: e.target.value })}
                    placeholder="10.5"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  />
                  <select
                    value={formData.weightUnit}
                    onChange={(e) => setFormData({ ...formData, weightUnit: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="kg">kg</option>
                    <option value="lb">lb</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Height</label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={formData.heightValue}
                    onChange={(e) => setFormData({ ...formData, heightValue: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">0 ft</option>
                    {[...Array(8)].map((_, i) => (
                      <option key={i} value={i}>{i} ft</option>
                    ))}
                  </select>
                  <select
                    value={formData.heightUnit}
                    onChange={(e) => setFormData({ ...formData, heightUnit: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">inches</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i} value={i}>{i} inches</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="hidden lg:block border-t pt-8 mt-8">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4">Birth Information</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mt-4 lg:mt-0">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Premature Birth</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, prematureBirth: "Yes" })}
                    className={`rounded-xl py-3 font-medium transition ${
                      formData.prematureBirth === "Yes"
                        ? "bg-[#1e3a5f] text-white lg:shadow-md"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, prematureBirth: "No" })}
                    className={`rounded-xl py-3 font-medium transition ${
                      formData.prematureBirth === "No"
                        ? "bg-[#1e3a5f] text-white lg:shadow-md"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {formData.prematureBirth === "Yes" && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">If yes, Weeks premature</label>
                  <select
                    value={formData.weeksPrematurity}
                    onChange={(e) => setFormData({ ...formData, weeksPrematurity: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="">3 weeks</option>
                    {[...Array(20)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1} weeks</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Birth Weight</label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.birthWeight}
                    onChange={(e) => setFormData({ ...formData, birthWeight: e.target.value })}
                    placeholder="2 kg"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  />
                  <select
                    value={formData.birthWeightUnit}
                    onChange={(e) => setFormData({ ...formData, birthWeightUnit: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20"
                  >
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="hidden lg:block border-t pt-8 mt-8">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4">Medical Information</h2>
            </div>
            
            <div className="mt-4 lg:mt-0">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Any known health conditions, allergies or special needs
              </label>
              <textarea
                value={formData.healthConditions}
                onChange={(e) => setFormData({ ...formData, healthConditions: e.target.value })}
                placeholder="Write here..."
                rows={3}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 focus:border-[#1e3a5f] focus:outline-none lg:focus:ring-2 lg:focus:ring-[#1e3a5f]/20 resize-none lg:rows-4"
              />
            </div>
            </>
            )}

            <div className="hidden lg:flex justify-end mt-10">
              <button
                onClick={handleSubmit}
                disabled={dateError}
                className={`px-12 py-4 rounded-xl bg-[#1e3a5f] text-white font-semibold shadow-lg hover:bg-[#152e4a] transition-all hover:shadow-xl ${dateError ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isEditMode ? "Update Profile" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white px-5 pb-8 pt-4 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <button
          onClick={handleSubmit}
          disabled={dateError}
          className={`w-full rounded-xl bg-[#1e3a5f] py-4 text-base font-semibold text-white shadow-md transition-transform active:scale-98 ${dateError ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isEditMode ? "Update Profile" : "Save"}
        </button>
        <div className="mt-4 flex justify-center">
          <div className="h-1 w-32 rounded-full bg-gray-800" />
        </div>
      </div>

      <div className="hidden lg:block h-16"></div>
    </div>
  );
}
